import { Button, Text } from "@chakra-ui/react";
import { ReactNode, useState } from "react";
import useApiRequest from "../../lib/hooks/useRequest";

interface Product {
  displayName: "Starter" | "Plus" | "Premium";
  planType: "standard" | "plus" | "premium" | "standardFlex" | "plusFlex" | "premiumFlex";
  mode: "fourMonths" | "oneYear";
  isFlex: boolean;
  maxEmployees: number;
  setupFee: number;
  monthlyPrice: number;
}

const products: Product[] = [
  {
    displayName: "Starter",
    planType: "standard",
    mode: "fourMonths",
    isFlex: true,
    maxEmployees: 15,
    setupFee: 1500,
    monthlyPrice: 275,
  },
  {
    displayName: "Starter",
    planType: "standard",
    isFlex: false,
    mode: "oneYear",
    maxEmployees: 15,
    setupFee: 1190,
    monthlyPrice: 249,
  },
  {
    displayName: "Plus",
    planType: "plus",
    mode: "fourMonths",
    isFlex: true,
    maxEmployees: 50,
    setupFee: 2085,
    monthlyPrice: 375,
  },
  {
    displayName: "Plus",
    planType: "plus",
    mode: "oneYear",
    isFlex: false,
    maxEmployees: 50,
    setupFee: 1700,
    monthlyPrice: 349,
  },
  {
    displayName: "Premium",
    planType: "premium",
    mode: "fourMonths",
    isFlex: true,
    maxEmployees: 100,
    setupFee: 3275,
    monthlyPrice: 425,
  },
  {
    displayName: "Premium",
    planType: "premium",
    mode: "oneYear",
    isFlex: false,
    maxEmployees: 100,
    setupFee: 2800,
    monthlyPrice: 399,
  },
];

export const SubscriptionContent = ({ footer }: { footer?: ReactNode }) => {
  const [mode, setMode] = useState<"fourMonths" | "oneYear">("oneYear");
  const filteredProducts = products.filter((product) => product.mode === mode);

  return (
    <div className="flex flex-col gap-6 p-0 sm:p-2">
      <div className="flex justify-center">
        <div className="inline-flex gap-2 bg-slate-200 rounded-lg p-1 w-full sm:w-auto">
          <Button
            onClick={() => setMode("oneYear")}
            className={`flex-1 sm:flex-none px-4 sm:px-6 ${mode === "oneYear" ? "bg-white shadow-sm" : "bg-transparent"}`}
            variant={mode === "oneYear" ? "solid" : "ghost"}
            size="md"
          >
            12 Monate
          </Button>
          <Button
            onClick={() => setMode("fourMonths")}
            className={`flex-1 sm:flex-none px-4 sm:px-6 ${mode === "fourMonths" ? "bg-white shadow-sm" : "bg-transparent"}`}
            variant={mode === "fourMonths" ? "solid" : "ghost"}
            size="md"
          >
            4 Monate (Flex)
          </Button>
        </div>
      </div>

      <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
        {filteredProducts.map((product) => (
          <ProductCmp key={`${product.planType}-${product.mode}`} product={product} />
        ))}
      </div>

      <div className="border-t border-slate-300 pt-4 space-y-2 text-sm text-slate-600">
        <Text>* alle Preise Brutto inkl. Mwst.</Text>
        <Text>
          ** Die Geld-Zurück-Garantie erhalten Sie bei Abschluss eines 12-monatigen Vertrages und bezieht sich auf die
          einmaligen Einrichtungskosten. Sollten Sie innerhalb eines Jahres nach Abschluss des Vertrages nicht
          mindestens 2 Einstellungen tätigen, erhalten Sie die gezahlten Einrichtungskosten vollständig erstattet.
        </Text>
      </div>

      {footer}
    </div>
  );
};

const ProductCmp = ({ product }: { product: Product }) => {
  const [hovers, setHovers] = useState<boolean>(false);
  const { apiRequest } = useApiRequest();

  const formatPrice = (price: number) => {
    return price.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const onClick = async () => {
    const res = await apiRequest<{
      paymentLink: string;
    }>(`stripe/paymentLink?planType=${product.planType}&planDuration=oneYear&planPaymentPeriod=monthly`, "GET");

    if (res.data?.paymentLink) {
      window.open(res.data.paymentLink, "_blank");
    }
  };

  return (
    <div
      className={`p-8 flex flex-col gap-4 border-2 relative rounded-lg border-transparent bg-transparent z-10`}
      onMouseEnter={() => setHovers(true)}
      onMouseLeave={() => setHovers(false)}
    >
      <fieldset
        className={`absolute top-0 left-0 right-0 bottom-0 border-2 border-blue-200 rounded-lg transition-colors duration-200 ${
          hovers ? "bg-slate-200" : "bg-transparent"
        }`}
      >
        <legend className="text-center text-sm text-slate-400 box-content whitespace-nowrap">
          <span className={`h-full overflow-hidden block max-w-0 text-xl font-bold`}></span>
        </legend>
      </fieldset>
      <div className="z-10 bg-transparent pointer-events-none flex flex-col gap-4">
        <img src={"/product-pic.png"} alt={product.displayName} className="w-32 h-32" />
        <div>
          <Text className="text-2xl font-bold">Paket {product.displayName}</Text>
          <Text className="text-slate-600 mt-1">Betriebe bis {product.maxEmployees} Mitarbeiter</Text>
        </div>
        <div className="border-t border-slate-300 pt-4 space-y-3">
          <div>
            <Text className="text-slate-600 text-sm mb-1">Einrichtungsgebühr:</Text>
            <Text className="text-xl font-bold">{formatPrice(product.setupFee)} € brutto</Text>
          </div>
          <div>
            <Text className="text-slate-600 text-sm mb-1">Monatlich:</Text>
            <Text className="text-xl font-bold">{formatPrice(product.monthlyPrice)} € brutto</Text>
          </div>
          <Text className="text-sm text-slate-500 pt-2 border-t border-slate-200">
            Mindestlaufzeit: {product.mode === "oneYear" ? "12" : "4"} Monate
          </Text>
        </div>
      </div>
      <div className="mt-auto flex flex-col gap-2 z-20 pt-4">
        <Button className="w-full" colorScheme="blue" onClick={onClick}>
          Paket auswählen
        </Button>
      </div>
    </div>
  );
};
